import React from 'react';
import Modal from 'components/ModalPopup';
import { Button, Group, Select, Text } from '@mantine/core';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { jobFamilyService } from 'services/lcms/jobFamily.service';

const filterSchema = z.object({
  jobFamilyGroupID: z.string().optional(),
  jobfamilyid: z.string().optional(),
  division: z.string().optional()
});

type FilterFormData = z.infer<typeof filterSchema>;

interface FilterCompetencyManagementProps {
  opened: boolean;
  onClose: () => void;
  onSubmitForm: (_data: { jobfamilyid?: string; division?: string }) => void;
  defaultData?: {
    jobfamilyid?: string;
    division?: string;
  };
}

const FilterCompetencyManagement: React.FC<FilterCompetencyManagementProps> = ({
  opened,
  onClose,
  onSubmitForm,
  defaultData
}) => {
  const [jobFamilyGroupSearch, setJobFamilyGroupSearch] = React.useState('');
  const [businessUnitSearch, setBusinessUnitSearch] = React.useState('');
  const [selectedJobFamilyGroupID, setSelectedJobFamilyGroupID] = React.useState<string>('');

  const { control, handleSubmit, reset, watch, setValue } = useForm<FilterFormData>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      jobFamilyGroupID: '',
      jobfamilyid: defaultData?.jobfamilyid || '',
      division: defaultData?.division || ''
    }
  });

  const watchJobFamilyGroupID = watch('jobFamilyGroupID');
  const watchJobFamilyID = watch('jobfamilyid');

  // Validation: if jobFamilyGroup is selected, jobFamily must also be selected
  const isValidFilter = React.useMemo(() => {
    // If Job Family Group is selected but Job Family is not, invalid
    if (watchJobFamilyGroupID && !watchJobFamilyID) {
      return false;
    }
    // If at least one field has value, it's valid
    return !!(watchJobFamilyID || watchJobFamilyGroupID || watch('division'));
  }, [watchJobFamilyGroupID, watchJobFamilyID, watch('division')]);

  const showValidationError = React.useMemo(() => {
    return watchJobFamilyGroupID && !watchJobFamilyID;
  }, [watchJobFamilyGroupID, watchJobFamilyID]);

  // Fetch Job Family Groups
  const { data: jobFamilyGroupsData, isLoading: isLoadingJobFamilyGroups } = useQuery({
    queryKey: ['jobFamilyGroups', jobFamilyGroupSearch],
    queryFn: () =>
      jobFamilyService.getJobFamilyGroups({
        search: jobFamilyGroupSearch,
        pageNumber: 1,
        pageSize: 10
      }),
    enabled: opened
  });

  // Fetch Job Families based on selected Job Family Group
  const { data: jobFamiliesData, isLoading: isLoadingJobFamilies } = useQuery({
    queryKey: ['jobFamilies', selectedJobFamilyGroupID],
    queryFn: () =>
      jobFamilyService.getJobFamilies({
        jobFamilyGroupID: selectedJobFamilyGroupID,
        pageNumber: 1,
        pageSize: 10
      }),
    enabled: !!selectedJobFamilyGroupID && opened
  });

  // Fetch Business Units
  const { data: businessUnitsData, isLoading: isLoadingBusinessUnits } = useQuery({
    queryKey: ['businessUnits', businessUnitSearch],
    queryFn: () =>
      jobFamilyService.getBusinessUnits({
        search: businessUnitSearch,
        pageNumber: 1,
        pageSize: 10
      }),
    enabled: opened
  });

  // Update selected job family group ID when selection changes
  React.useEffect(() => {
    if (watchJobFamilyGroupID) {
      setSelectedJobFamilyGroupID(watchJobFamilyGroupID);
      // Reset job family when group changes
      setValue('jobfamilyid', '');
    }
  }, [watchJobFamilyGroupID, setValue]);

  const onSubmit = (data: FilterFormData) => {
    // Validate: if jobFamilyGroup is selected, jobFamily must also be selected
    if (data.jobFamilyGroupID && !data.jobfamilyid) {
      return; // Don't submit if validation fails
    }

    onSubmitForm({
      jobfamilyid: data.jobfamilyid || undefined,
      division: data.division || undefined
    });
    onClose();
  };

  const handleCancel = () => {
    reset();
    setSelectedJobFamilyGroupID('');
    setJobFamilyGroupSearch('');
    setBusinessUnitSearch('');
    onClose();
  };

  const handleClear = () => {
    reset();
    setSelectedJobFamilyGroupID('');
    setJobFamilyGroupSearch('');
    setBusinessUnitSearch('');
    onSubmitForm({ jobfamilyid: undefined, division: undefined });
    onClose();
  };

  const jobFamilyGroupOptions =
    jobFamilyGroupsData?.data.map((group) => ({
      value: group.jobFamilyGroupID,
      label: group.jobFamilyGroupName
    })) || [];

  const jobFamilyOptions =
    jobFamiliesData?.data.map((family) => ({
      value: family.jobFamilyID,
      label: family.jobFamilyName
    })) || [];

  const businessUnitOptions =
    businessUnitsData?.data.map((unit) => ({
      value: unit.buCode,
      label: unit.buName
    })) || [];

  return (
    <Modal title='Filter Data' size='lg' opened={opened} close={onClose} withCloseButton>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='jobFamilyGroupID'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label='Job Family Group'
              placeholder='Select Job Family Group'
              searchable
              clearable
              data={jobFamilyGroupOptions}
              onSearchChange={setJobFamilyGroupSearch}
              nothingFoundMessage={isLoadingJobFamilyGroups ? 'Loading...' : 'No options'}
              mb='md'
            />
          )}
        />
        <Controller
          name='jobfamilyid'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label='Job Family'
              placeholder='Select Job Family'
              description={
                watchJobFamilyGroupID ? 'Required when Job Family Group is selected' : ''
              }
              searchable
              clearable
              data={jobFamilyOptions}
              disabled={!selectedJobFamilyGroupID}
              error={showValidationError ? 'Please select a Job Family' : undefined}
              nothingFoundMessage={
                isLoadingJobFamilies
                  ? 'Loading...'
                  : !selectedJobFamilyGroupID
                    ? 'Please select a Job Family Group first'
                    : 'No options'
              }
              mb='md'
            />
          )}
        />
        <Controller
          name='division'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label='Business Unit'
              placeholder='Select Business Unit'
              searchable
              clearable
              data={businessUnitOptions}
              onSearchChange={setBusinessUnitSearch}
              nothingFoundMessage={isLoadingBusinessUnits ? 'Loading...' : 'No options'}
              mb='md'
            />
          )}
        />
        {showValidationError && (
          <Text size='sm' c='red' mb='md'>
            You must select a Job Family when filtering by Job Family Group.
          </Text>
        )}
        <Group justify='flex-end' mt={24}>
          <Button variant='outline' onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant='default' onClick={handleClear}>
            Clear
          </Button>
          <Button type='submit' disabled={!isValidFilter}>
            Filter
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default FilterCompetencyManagement;
